<template>
  <div class="j-slider" :class="rootClasses">
    <div class="j-slider-track" @click="onSliderClick" ref="slider">
      <div class="j-slider-fill" :style="barStyle" />
      <div
        class="j-slider-thumb-wrapper"
        role="slider"
        :aria-valuenow="value1"
        :aria-valuemin="min"
        :aria-valuemax="max"
        aria-orientation="horizontal"
        :aria-disabled="disabled"
        @dragstart="onDragStart"
        @dragend="onDragEnd"
        :class="{ 'is-dragging': dragging }"
        :style="wrapperStyle"
      >
        <div
          class="j-slider-thumb"
          :tabindex="disabled ? -1 : 0"
          @mousedown="onButtonDown"
          @touchstart="onButtonDown"
          @keydown.left.prevent="onLeftKeyDown"
          @keydown.right.prevent="onRightKeyDown"
          @keydown.down.prevent="onLeftKeyDown"
          @keydown.up.prevent="onRightKeyDown"
          @keydown.home.prevent="onHomeKeyDown"
          @keydown.end.prevent="onEndKeyDown"
        ></div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
interface Props {
  modelValue?: number
  min?: number
  max?: number
  step?: number
  disabled?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: 0,
  min: 0,
  max: 100,
  step: 1,
  disabled: false
})

const emits = defineEmits(['update:modelValue', 'change'])

const value1 = ref(null)
const dragging = ref(false)
const startX = ref(0)
const startPosition = ref(0)
const newPosition = ref(null)
const oldValue = ref(0)
const isTrackClickDisabled = ref(false)

onMounted(() => {
  oldValue.value = props.modelValue
})

const barSize = computed(() => {
  return `${(100 * (value1.value - props.min)) / (props.max - props.min)}%`
})

const barStart = computed(() => {
  return '0%'
})

const precision = computed(() => {
  const precisions = [props.min, props.max, props.step].map(item => {
    let decimal = ('' + item).split('.')[1]
    return decimal ? decimal.length : 0
  })
  return Math.max(...precisions)
})

const barStyle = computed(() => {
  return {
    width: barSize.value,
    left: barStart.value,
  }
})

const slider = ref<HTMLElement>()

const sliderSize = computed(() => {
  return slider.value?.clientWidth || 1
})

const rootClasses = computed(() => {
  return {
    'is-dragging': dragging.value,
    'is-disabled': props.disabled,
  }
})

watch(
  () => props.modelValue,
  () => {
    setValues(props.modelValue)
  },
)

watch(
  () => value1.value,
  () => {
    onInternalValueUpdate()
  },
)

watch(
  () => props.min,
  () => {
    setValues(props.modelValue)
  },
)

watch(
  () => props.max,
  () => {
    setValues(props.modelValue)
  },
)

const setValues = newValue => {
  if (props.min > props.max) {
    return
  }
  value1.value = isNaN(newValue)
    ? props.min
    : Math.min(props.max, Math.max(props.min, newValue))
}

const onInternalValueUpdate = () => {
  if (!dragging.value) {
    emitValue('update:modelValue')
  }
}

const onSliderClick = event => {
  if (props.disabled || isTrackClickDisabled.value) return
  const sliderOffsetLeft = slider.value.getBoundingClientRect().left
  const percent = ((event.clientX - sliderOffsetLeft) / sliderSize.value) * 100
  const targetValue = props.min + (percent * (props.max - props.min)) / 100
  const diffFirst = Math.abs(targetValue - value1.value)
  if (diffFirst < props.step / 2) return
  setPosition(percent)
  emits('change')
}

const currentPosition = computed(() => {
  return `${((props.modelValue - props.min) / (props.max - props.min)) * 100}%`
})

const wrapperStyle = computed(() => {
  return { left: currentPosition.value }
})

const onButtonDown = event => {
  if (props.disabled) return
  event.preventDefault()
  onDragStart(event)
  if (typeof window !== 'undefined') {
    document.addEventListener('mousemove', onDragging)
    document.addEventListener('touchmove', onDragging)
    document.addEventListener('mouseup', onDragEnd)
    document.addEventListener('touchend', onDragEnd)
    document.addEventListener('contextmenu', onDragEnd)
  }
}

const onLeftKeyDown = () => {
  if (props.disabled || props.modelValue === props.min) return
  newPosition.value =
    parseFloat(currentPosition.value) -
    (props.step / (props.max - props.min)) * 100
  setPosition(newPosition.value)
  emits('change')
}

const onRightKeyDown = () => {
  if (props.disabled || props.modelValue === props.max) return
  newPosition.value =
    parseFloat(currentPosition.value) +
    (props.step / (props.max - props.min)) * 100
  setPosition(newPosition.value)
  emits('change')
}

const onHomeKeyDown = () => {
  if (props.disabled || props.modelValue === props.min) return
  newPosition.value = 0
  setPosition(newPosition.value)
  emitValue('change')
}

const onEndKeyDown = () => {
  if (props.disabled || props.modelValue === props.max) return
  newPosition.value = 100
  setPosition(newPosition.value)
  emitValue('change')
}

const onDragStart = event => {
  dragging.value = true
  // $emit("dragstart");
  if (event.type === 'touchstart') {
    event.clientX = event.touches[0].clientX
  }
  startX.value = event.clientX
  startPosition.value = parseFloat(currentPosition.value)
  newPosition.value = startPosition.value
}

const onDragging = event => {
  if (dragging.value) {
    if (event.type === 'touchmove') {
      event.clientX = event.touches[0].clientX
    }
    const diff = ((event.clientX - startX.value) / sliderSize.value) * 100
    newPosition.value = startPosition.value + diff
    setPosition(newPosition.value)
  }
}

const onDragEnd = () => {
  dragging.value = false
  isTrackClickDisabled.value = true
  setTimeout(() => {
    isTrackClickDisabled.value = false
  }, 0)

  if (props.modelValue !== oldValue.value) {
    emits('change')
  }
  setPosition(newPosition.value)
  if (typeof window !== 'undefined') {
    document.removeEventListener('mousemove', onDragging)
    document.removeEventListener('touchmove', onDragging)
    document.removeEventListener('mouseup', onDragEnd)
    document.removeEventListener('touchend', onDragEnd)
    document.removeEventListener('contextmenu', onDragEnd)
  }
}

const setPosition = percent => {
  if (percent === null || isNaN(percent)) return
  if (percent < 0) {
    percent = 0
  } else if (percent > 100) {
    percent = 100
  }
  const stepLength = 100 / ((props.max - props.min) / props.step)
  const steps = Math.round(percent / stepLength)
  let value: number =
    ((steps * stepLength) / 100) * (props.max - props.min) + props.min
  value = parseFloat(value.toFixed(precision.value))
  emits('update:modelValue', value)
  if (!dragging.value && value !== oldValue.value) {
    oldValue.value = value
  }
}

const emitValue = type => {
  emits(type, value1.value)
}

;(() => {
  isTrackClickDisabled.value = false
  setValues(props.modelValue)
})()
</script>

<style lang="scss" scoped>
@use '@/assets/styles/scss/vue' as *;

.j-slider {
  --global-radius: 6px;
  --global-radius-rounded: 290486px;
  --slider-radius: var(--global-radius);
  --slider-track-background: #{$jps-gray-100};
  --slider-track-radius: var(--global-radius);
  --slider-track-border: 0px solid #{$jps-gray-500};
  --slider-track-shadow: 0px 0px 0px #{$jps-gray-500};
  --slider-thumb-background: #{$black};
  --slider-thumb-radius: var(--global-radius);
  --slider-thumb-border: 3px solid #{$jps-gray-50};
  --slider-thumb-shadow: none;
  --slider-thumb-to-track-ratio: 2.66;
  --slider-tick-to-track-ratio: 0.5;
  --slider-tick-width: 6px;
  --slider-tick-radius: var(--global-radius);
  --slider-tick-background: #{$jps-gray-100};
  --slider-mark-size: 0.75rem;
  margin: 1rem 0;
  background: transparent;
  width: 100%;

  .j-slider-track {
    display: flex;
    align-items: center;
    position: relative;
    cursor: pointer;
    background: $jps-gray-50;
    border-radius: var(--slider-radius);
  }
  .j-slider-fill {
    position: absolute;
    height: 100%;
    box-shadow: var(--slider-track-shadow);
    background: var(--slider-track-background);
    border-radius: var(--slider-track-radius);
    border: var(--slider-track-border);
    background: $jps-key-red;
    top: 50%;
    transform: translateY(-50%);
  }
  .j-slider-thumb-wrapper {
    display: inline-flex;
    align-items: center;
    position: absolute;
    cursor: grab;
    transform: translate(-50%, -50%);
    top: 50%;
    flex-direction: column;

    .j-slider-thumb {
      box-shadow: var(--slider-thumb-shadow);
      border: var(--slider-thumb-border);
      border-radius: var(--global-radius-rounded);
      background-color: $jps-key-red-hover;
      &:focus {
        transform: scale(1.25);
      }
    }

    &.is-dragging {
      cursor: grabbing;
      .j-slider-thumb {
        transform: scale(1.25);
      }
    }
  }

  &.is-disabled {
    .j-slider-track {
      cursor: not-allowed;
      opacity: 0.5;
    }
    .j-slider-thumb-wrapper {
      cursor: not-allowed;
      .j-slider-thumb {
        transform: scale(1);
      }
    }
  }

  --track-height: calc(1rem / var(--slider-thumb-to-track-ratio));
  --tick-height: calc(var(--track-height) * var(--slider-tick-to-track-ratio));
  --thumb-size: 1rem;

  .j-slider-track {
    height: var(--track-height);
  }

  .j-slider-thumb {
    height: var(--thumb-size);
    width: var(--thumb-size);
  }

  .j-slider-tick {
    height: var(--tick-height);
  }

  .j-slider-tick-label {
    font-size: var(--slider-mark-size);
    position: absolute;
    top: calc(var(--track-height) / 2 + 2px);
    left: 50%;
    transform: translateX(-50%);
  }
}
</style>
